import React from "react";
import * as Yup from "yup";
import Validation from "shared/form/Validation";
import Tokens from "shared/utils/storage/tokens";
import { useCopies } from "shared/hooks/useCopies";
import { ConsoleRouteMap } from "apps/console/routes/routes";
import { useAcceptUserInvitation, useResetPassword } from "./dataloader";
import { matchRoutes, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Base64Util } from "shared/utils/base64";
import { useAuth } from "shared/context/auth";
import QueryParamKey from "../../../../shared/enums/QueryParamKey";

/**
  Password Reset Link
  // use encoded token
  http://127.0.0.1:3000/reset-password?token=TRANSIENT_TOKEN&cuId=USER_ID
  Accept User Invitation Link
  http://127.0.0.1:3000/accept-invite?token=TRANSIENT_TOKEN&cuId=USER_ID
  Get Transient Token from api http://localhost:8000/testEnc/CONSOLE_USER_ID
*/
export default function useResetPasswordService() {
  const location = useLocation();
  const copies = useCopies();
  const [displayPassword, setDisplayPassword] = React.useState<boolean>(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { logout } = useAuth();
  const userId = Base64Util.decode(searchParams.get(QueryParamKey.CONSOLE_USER_ID));
  const transientToken = searchParams.get(QueryParamKey.TOKEN);

  const { mutateAsync: resetPassword } = useResetPassword();
  const { mutateAsync: acceptUserInvitation } = useAcceptUserInvitation();

  const isAcceptUserFlow = React.useMemo(
    () => Boolean(matchRoutes([ConsoleRouteMap.ACCEPT_USER_INVITE], location.pathname)),
    [location.pathname]
  );

  React.useEffect(() => {
    if (transientToken) {
      logout();
      Tokens.setTransitToken(transientToken);
    } else {
      navigate(ConsoleRouteMap.HOME.path);
    }
  }, [logout, navigate, transientToken]);

  const initialValues = React.useMemo(
    () => ({
      password: "",
      confirmPassword: ""
    }),
    []
  );

  const validationSchema = React.useMemo(
    () =>
      Yup.object().shape({
        password: Validation.passwordValidation({ required: true, copies }),
        confirmPassword: Validation.yupIsReq({
          required: true,
          copies
        }).test("confirmPassword", copies.get("password.mismatch"), function (value) {
          return this.parent.password === value;
        })
      }),
    [copies]
  );

  const navigateToLogin = React.useCallback(() => {
    navigate(`/${ConsoleRouteMap.LOGIN.path}`);
    Tokens.setTransitToken("");
  }, [navigate]);

  const handleResetPassword = React.useCallback(
    values => {
      resetPassword({
        ...values,
        id: userId
      }).then(() => {
        navigateToLogin();
      });
    },
    [navigateToLogin, resetPassword, userId]
  );

  const handleAcceptUserInvitation = React.useCallback(
    values => {
      acceptUserInvitation({
        ...values,
        id: userId
      }).then(() => {
        navigateToLogin();
      });
    },
    [acceptUserInvitation, navigateToLogin, userId]
  );

  const formHelper = React.useMemo(
    () => ({
      handleSubmit: isAcceptUserFlow ? handleAcceptUserInvitation : handleResetPassword,
      initialValues,
      validationSchema,
      headerText: isAcceptUserFlow ? copies.get("set.password") : copies.get("reset.password"),
      submitButtonText: isAcceptUserFlow ? copies.get("set.password") : copies.get("reset.password")
    }),
    [copies, handleAcceptUserInvitation, handleResetPassword, initialValues, isAcceptUserFlow, validationSchema]
  );

  return {
    formHelper,
    displayPassword,
    setDisplayPassword
  };
}
